// SUMMARY: Deterministic cleanup of assistant Markdown before it reaches the (math-plugin-less) renderer.
// gpt-oss intermittently ignores the SYSTEM_PROMPT no-LaTeX rule and emits `\(…\)` / `\[…\]` / `$…$` plus
// U+2011 non-breaking hyphens; relying on the prompt alone is unreliable, so this guarantees clean output
// regardless of the model. Strips math DELIMITERS (keeping the expression), converts the most common LaTeX
// commands to plain Unicode, normalizes non-ASCII hyphens, and REMOVES unbalanced `**` bold markers (the
// model opens bold without ever closing it, so remark would render a literal `**`). Pure + unit-tested.

// ===============================
// SECTION: MATH DELIMITER STRIPPING
// ===============================

// EXAMPLE: "\(x^2=16\)" → "x^2=16" · "\[ a+b \]" → "a+b" · "$x$" → "x" · "$5 to $9" → unchanged (currency)
export function stripMathDelimiters(text: string): string {
	return (
		text
			// block \[ … \] and \( … \) inline — unambiguously LaTeX, never legitimate prose
			.replace(/\\\[([\s\S]*?)\\\]/g, (_, body) => `\n${convertMathCommands(body.trim())}\n`)
			.replace(/\\\(([\s\S]*?)\\\)/g, (_, body) => convertMathCommands(body.trim()))
			// $$ … $$ display math
			.replace(/\$\$([\s\S]*?)\$\$/g, (_, body) => convertMathCommands(body.trim()))
			// $ … $ inline math, but ONLY when it looks like math (a LaTeX command or ^ _ { } = ), so prices
			// like "$5 to $9" are left untouched
			.replace(/(?<!\\)\$([^$\n]+?)\$/g, (match, body) =>
				/[\\^_{}=]/.test(body) ? convertMathCommands(body.trim()) : match
			)
	)
}

// ===============================
// SECTION: LATEX COMMAND → PLAIN
// ===============================

const MATH_SYMBOLS: Record<string, string> = {
	pm: '±',
	mp: '∓',
	times: '×',
	cdot: '·',
	div: '÷',
	leq: '≤',
	geq: '≥',
	neq: '≠',
	approx: '≈',
	equiv: '≡',
	Rightarrow: '⇒',
	Leftarrow: '⇐',
	rightarrow: '→',
	leftarrow: '←',
	infty: '∞',
	sum: '∑',
	prod: '∏',
	int: '∫',
	alpha: 'α',
	beta: 'β',
	gamma: 'γ',
	delta: 'δ',
	pi: 'π',
	theta: 'θ',
	lambda: 'λ',
	mu: 'μ',
	sigma: 'σ',
	omega: 'ω',
	Omega: 'Ω',
}

function convertMathCommands(expr: string): string {
	return expr
		.replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, '($1)/($2)') // \frac{a}{b} → (a)/(b)
		.replace(/\\sqrt\s*\{([^{}]*)\}/g, '√($1)') // \sqrt{x} → √(x)
		.replace(/\^\{([^{}]*)\}/g, '^$1') // x^{2} → x^2
		.replace(/_\{([^{}]*)\}/g, '_$1') // a_{i} → a_i
		.replace(/\\(left|right)\b/g, '') // \left( \right) → ( )
		.replace(/\\[,;:!]/g, ' ') // thin-space commands → space
		.replace(/\\quad|\\qquad/g, '  ')
		.replace(/\\([a-zA-Z]+)/g, (full, name: string) => MATH_SYMBOLS[name] ?? full) // known symbols, else leave
		.replace(/[ \t]{2,}/g, ' ')
		.trim()
}

// ===============================
// SECTION: UNBALANCED BOLD MARKERS
// ===============================

// The model frequently opens bold to emphasize a value (`**6 750 €`) but never emits a valid closer, or emits a
// second opener-style `**` that cannot close (`**Netto: **6 045 €`). remark then renders those as literal `**`.
// This greedily pairs each `**` opener with the next valid closer on the same line and strips any that are left
// unpaired, so a stray delimiter never reaches the reader. Genuine `**bold**` pairs and literal operators like
// `2**3` (no word boundary before `**`) are left untouched; fenced/inline code is skipped entirely.

/** Remove `**` delimiters that don't form a valid bold pair, so no literal `**` survives to the renderer. */
export function balanceBoldMarkers(text: string): string {
	let inFence = false
	return text
		.split('\n')
		.map((line) => {
			if (/^\s*(```|~~~)/.test(line)) {
				inFence = !inFence
				return line
			}
			return inFence ? line : fixLineBold(line)
		})
		.join('\n')
}

// Fix one line, preserving inline `code` spans (a `**` inside them is literal - e.g. Python `2**3`).
function fixLineBold(line: string): string {
	return line
		.split(/(`[^`]*`)/)
		.map((segment) => (segment.startsWith('`') && segment.endsWith('`') ? segment : stripUnpairedBold(segment)))
		.join('')
}

function stripUnpairedBold(segment: string): string {
	// Only runs of EXACTLY two asterisks are bold delimiters (leave `*italic*` / `***both***` alone).
	const delimiters: number[] = []
	for (const match of segment.matchAll(/\*+/g)) {
		if (match[0].length === 2) delimiters.push(match.index)
	}
	if (delimiters.length === 0) return segment

	const strip = new Set<number>()
	let openIndex: number | null = null
	for (const index of delimiters) {
		const prevChar = index > 0 ? segment[index - 1] : undefined
		const nextChar = segment[index + 2]
		// Opener: at a word boundary (start/whitespace before) and hugging its content (non-space after).
		const canOpen = (prevChar === undefined || /\s/.test(prevChar)) && nextChar !== undefined && !/\s/.test(nextChar)
		// Closer: hugs the preceding content (non-space before).
		const canClose = prevChar !== undefined && !/\s/.test(prevChar)

		if (openIndex === null) {
			if (canOpen) openIndex = index // else a literal `**` (e.g. `2**3`) - leave it
		} else if (canClose) {
			openIndex = null // valid pair, keep both delimiters
		} else if (canOpen) {
			strip.add(openIndex) // the previous opener never closed
			openIndex = index
		}
	}
	if (openIndex !== null) strip.add(openIndex) // trailing unclosed opener

	if (strip.size === 0) return segment
	let result = segment
	for (const index of [...strip].sort((a, b) => b - a)) {
		result = result.slice(0, index) + result.slice(index + 2)
	}
	return result
}

// ===============================
// SECTION: LEAKED REASONING
// ===============================

// gpt-oss intermittently leaks its hidden reasoning/analysis channel INTO the visible answer: a real turn
// began "We need to simulate web search.Search web for … Let's produce answer with sections:" before the
// actual answer's first "## " heading. The prompt forbids it, but (as with LaTeX) compliance is
// intermittent — so strip it deterministically. Only a PREFIX before the first heading is ever considered,
// and only when it carries several unmistakable deliberation markers, so genuine heading-less answers and
// legitimate introductions are never touched.
// EXAMPLE: "We need to simulate web search. Let's produce answer with sections: ## 📄 Biografie…" → "## 📄 Biografie…"
const REASONING_MARKERS = [
	/\bwe (need to|should|could|can|must|'d better|will|might)\b/i,
	/\blet'?s (produce|provide|give|answer|approximate|be safe|say)\b/i,
	/\bthe user (likely|probably|expects|asks|wants|is asking)\b/i,
	/\bI'?d better\b/i,
	/\b(actually )?quick mental\b/i,
	/\bprovide (a |that |brief |generic )/i,
	/\bbetter answer:\b/i,
	/\bsimulate (a )?web search\b/i,
	/\bresult snippet:\b/i,
	/\bnot sure\.\b/i,
	/\buncertainty\.\b/i,
] as const
const MIN_REASONING_MARKERS = 2
const MAX_REASONING_PREFIX_CHARS = 2000

/**
 * Drop a leaked chain-of-thought PREFIX before the answer's first Markdown heading. Fires only when the
 * prefix is short enough to be deliberation (not a whole answer) and matches at least two distinct
 * deliberation markers — a normal introduction never does.
 */
export function stripLeakedReasoning(text: string): string {
	const heading = text.match(/(^|\n)#{2,4}\s/)
	if (!heading || heading.index === undefined) return stripSimulatedSearch(text)
	const prefix = text.slice(0, heading.index)
	if (!prefix.trim() || prefix.length > MAX_REASONING_PREFIX_CHARS) return stripSimulatedSearch(text)
	const hits = REASONING_MARKERS.filter((marker) => marker.test(prefix)).length
	if (hits < MIN_REASONING_MARKERS) return stripSimulatedSearch(text)
	return text.slice(heading.index).replace(/^\n+/, '')
}

// A FABRICATED search transcript: the plain (non-search) model narrating searches it never ran, then
// answering from the invented "results". Observed verbatim on a fixture question that fell through the
// search gate: `Searching web for Geng LCK next opponent.Searching for "Geng LCK next opponent".Search
// results:Search for "Geng player LCK".Search result snippet: "Geng (GENG) is a Korean mid laner for T1"`.
// It carries no Markdown heading, so the prefix-before-heading rule above never saw it.
// EXAMPLE: `Searching web for X.Search results:Search result snippet: "…"Real answer.` → `Real answer.`
const SIMULATED_SEARCH_SEGMENT =
	/(?:searching\s+(?:the\s+)?web\s+for\b[^.]*\.|searching\s+for\s+"[^"]*"\.?|search\s+for\s+"[^"]*"\.?|search\s+results?:|search\s+result\s+snippet:\s*"[^"]*"\.?)\s*/gi

/** Minimum fabricated segments before the text is treated as a simulated transcript rather than prose. */
const MIN_SIMULATED_SEGMENTS = 2

/**
 * Remove a fabricated search transcript, keeping whatever real answer follows it. Requires several distinct
 * fabricated segments so a genuine sentence like "I'll start by searching for the schedule" is untouched.
 */
function stripSimulatedSearch(text: string): string {
	const matches = text.match(SIMULATED_SEARCH_SEGMENT)
	if (!matches || matches.length < MIN_SIMULATED_SEGMENTS) return text
	const stripped = text.replace(SIMULATED_SEARCH_SEGMENT, '').replace(/^[\s.]+/, '')
	// Never strip the whole message down to nothing — an empty bubble is worse than the leak.
	return stripped.trim() ? stripped : text
}

// ===============================
// SECTION: ENTRY POINT
// ===============================

/** Render-time normalization for assistant Markdown: leaked-reasoning strip + clean LaTeX + ASCII-safe hyphens + balanced bold. */
export function sanitizeAssistantMarkdown(text: string): string {
	const cleaned = stripMathDelimiters(stripLeakedReasoning(text))
		.replace(/‑/g, '-') // non-breaking hyphen → ASCII (gpt-oss emits these in code/commands)
		.replace(/‐/g, '-') // hyphen
	return balanceBoldMarkers(cleaned)
}
